import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const pathnameToTitle: Record<string, string> = {
    '/': 'Home',
    '/dashboard': 'Dashboard',
    '/login': 'Login',
    '/signup': 'Sign Up',
    '/resetPassword': 'Reset Password',
    '/forgotpassword': 'Forgot Password',
    '/activities': 'Activities',
    '/activities/create': 'Create Activity',
    '/languages': 'Languages',
    '/messages': 'Messages',
    '/payment': 'Payment',
    '/paymentLPI': 'Payment',
    '/payment/water-bill-payment-records': 'Water Bill Payment Records',
    '/payment/payment-history': 'Payment History',
    '/organization_management': 'Organization Management',
    '/add_organization': 'Add Organization',
    '/elgin_today': 'Elgin Today',
    '/in_progress': 'In Progress',
    '/completed_activities': 'Completed Activities',
    '/search': 'Search',
    '/activity/start': 'Start Activity',
    '/saleforce_public_fields': 'Salesforce Public Fields',
    '/knowledgebase': 'Knowledge Base',
    '/permit-search': 'Permit Search',
    '/garbage-search': 'Garbage Search',
    '/feedback': 'Feedback',
    '/system_alerts': 'System Alerts',
    '/billing_maintenance_settings': 'Billing Maintenance Settings',
    '/bulk_push_notifications': 'Bulk Push Notifications',
    '/delinquency_notifications': 'Delinquency Notifications',
    '/view-all-services': 'All Services',
    '/boards-and-commissions': 'Boards and Commissions',
    '/add-stored-payment': 'Add Stored Payment',
    '/forms-browser': 'Forms Browser',
    '/account/create': 'Create Account',
    '/account/verificationRequired': 'Verification Required',
    '/businesslicense': 'Business License Renewal',
    '/news': 'News',
    '/events': 'Events',
    '/admin/content': 'Content Management',
    '/admin/content/create': 'Create Content',
    '/report': 'Report',
    '/sso/saml': 'Single Sign-On',
};

function getTitleFromPathname(pathname: string): string {
    if (pathnameToTitle[pathname]) {
        return pathnameToTitle[pathname];
    }

    // Handle dynamic routes
    if (pathname.startsWith('/account/')) return 'Account';
    if (pathname.startsWith('/activities/')) return 'Activity Details';
    if (pathname.startsWith('/languages/')) return 'Language Editor';
    if (pathname.startsWith('/users/view/')) return 'User Details';
    if (pathname.startsWith('/users/edit/')) return 'Edit User';
    if (pathname.startsWith('/users/')) return 'User Details';
    if (pathname.startsWith('/knowledgebase/')) return 'Knowledge Base Article';
    if (pathname.startsWith('/activity-view/')) return 'Activity Details';
    if (pathname.startsWith('/boards-and-commissions/')) return 'Board or Commission';
    if (pathname.startsWith('/news/')) return 'News Article';
    if (pathname.startsWith('/events/')) return 'Event Details';
    if (pathname.startsWith('/admin/content/edit/')) return 'Edit Content';

    // Fallback: convert path to readable title
    const segment = pathname.split('/').filter(Boolean).pop() || '';
    return segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Page';
}

export const RouteAnnouncer: React.FC = () => {
    const location = useLocation();
    const [announcement, setAnnouncement] = useState('');
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const title = getTitleFromPathname(location.pathname);
        document.title = `${title} | City of Elgin`;
        setAnnouncement(`Navigated to ${title}`);
    }, [location.pathname]);

    return (
        <div
            aria-live="polite"
            aria-atomic="true"
            role="status"
            className="visually-hidden"
        >
            {announcement}
        </div>
    );
};
